import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import FileDocumentArrowRightOutlineIcon from 'mdi-react/FileDocumentArrowRightOutlineIcon'
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useIsThreeStepIT } from '@/hooks/access'
import queries from '@/modules/admin/queries'

import { getReportExportUsingAPI } from '../exportFormats'
import { useReportsContext } from '../index.context'

import { OtherOptions } from './OtherOptions'

import type { ReportFilters, ReportSetupExportSettings } from '../../resources'
import adminResources from '../../resources'
import {
  EXPORT_OPTIONS,
  type ExportFiltersType,
  type ExportOptionsType,
} from '../../resources/reports/types'

export const SalaryDeductionUsageOptions: FC<{
  title: string
  filters: ReportFilters
  onExportOptionClick: (id: ExportOptionsType) => void
  reportSetupExportSettings?: ReportSetupExportSettings
}> = ({ title, filters, reportSetupExportSettings }) => {
  const isThreeStepIT = useIsThreeStepIT()

  const { data: reports } = useQuery(queries.adminSalaryDeductionUsageReports(filters), () =>
    adminResources.getSalaryDeductionUsageReports(filters)
  )

  const { customerSpecificReports } = useReportsContext()

  if (!reports) throw Error('We have encountered a problem while loading reports')
  const { fromDate, toDate } = filters
  if (!fromDate || !toDate) throw Error('Missing date range filters!')

  const companyNames = reports.map(report => report.customerName)
  let companyName = ''
  if (companyNames.length > 0) {
    companyName = companyNames[0]
  }

  const reportsData = reports.map(report => ({
    name: report.name,
    employeeNo: report.employeeNo,
    departmentCode: report.departmentCode,
    departmentName: report.departmentName,
    benefitMobile: report.benefitMobile,
    benefitMobileCeiling: report.benefitMobileCeiling ?? '',
    netAmount: report.amount,
    vat: report.vat ?? '',
    employeeAccountingCode: report.accountingCodeCostObject,
    organisationNumber: report.orgNo,
    companyName: report.customerName,
    posts: report.posts,
    accountingCode: report.accountingCode,
    project: report.projectUsage,
    totalAmount: report.amount + (report.vat ?? 0),
    currency: report.currency,
  }))

  const columnFormats = {
    benefitMobileCeiling: 'amount',
    project: 'number',
    totalAmount: 'amount',
    vat: 'amount',
    netAmount: 'amount',
    posts: 'number',
  }

  const handleReportExport = (formatName: ExportFiltersType['formatName']) => {
    getReportExportUsingAPI({
      reportName: 'salary-deduction-usage',
      formatName,
      isGlobal: filters.isGlobal,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      customerId: filters.selectedCustomer,
    })
  }

  const customerSpecificReport = customerSpecificReports.find(
    element => element.report === filters.selectedReportType
  )

  return (
    <>
      <OtherOptions
        title={title}
        reportsData={reportsData}
        columnFormats={columnFormats}
        filters={filters}
      />
      {reportSetupExportSettings && (
        <div className="grid grid-cols-2">
          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            className="mb-4 ml-0"
            id={EXPORT_OPTIONS.VISMA_LONN}
            onClick={() => handleReportExport(EXPORT_OPTIONS.VISMA_LONN)}
          >
            <Translation id="Visma Lønn V1" />
          </Button>

          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            className="mb-4 ml-0"
            id={EXPORT_OPTIONS.VISMA_LONN_V2}
            onClick={() => handleReportExport(EXPORT_OPTIONS.VISMA_LONN_V2)}
          >
            <Translation id="Visma Lønn V2" />
          </Button>

          <Button
            Icon={<FileDocumentArrowRightOutlineIcon />}
            id={EXPORT_OPTIONS.VISMA_LONN_V3}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.VISMA_LONN_V3)}
          >
            <Translation id="Visma Lønn V3" />
          </Button>

          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            id={EXPORT_OPTIONS.VISMA_PAYROLL}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.VISMA_PAYROLL)}
          >
            <Translation id="Visma Payroll" />
          </Button>

          <Button
            Icon={<FileDocumentArrowRightOutlineIcon />}
            id={EXPORT_OPTIONS.HULDT_OG_LILLEVIK}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.HULDT_OG_LILLEVIK)}
          >
            <Translation id="Huldt & Lillevik" />
          </Button>

          <Button
            id={EXPORT_OPTIONS.HULDT_OG_LILLEVIK_SDV}
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.HULDT_OG_LILLEVIK_SDV)}
          >
            <Translation id="Huldt & Lillevik Telenor SDV" />
          </Button>

          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            id={EXPORT_OPTIONS.SAP}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.SAP)}
          >
            <Translation id="SAP" />
          </Button>

          <Button
            Icon={<TrayArrowDownIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
            id={EXPORT_OPTIONS.SAP_V2}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.SAP_V2)}
          >
            <Translation id="SAP v2" />
          </Button>
          <Button
            id={EXPORT_OPTIONS.TRIPLETEX}
            Icon={<FileDocumentArrowRightOutlineIcon />}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.TRIPLETEX)}
          >
            <Translation id="Tripletex" />
          </Button>

          <Button
            id={EXPORT_OPTIONS.SD_WORKS_ADITRO}
            Icon={<FileDocumentArrowRightOutlineIcon />}
            className="mb-4 ml-0"
            onClick={() => handleReportExport(EXPORT_OPTIONS.SD_WORKS_ADITRO)}
          >
            <Translation id="SD Works" />
          </Button>

          {customerSpecificReport && (
            <Button
              Icon={<TrayArrowDownIcon />}
              onClick={() => handleReportExport(EXPORT_OPTIONS.CUSTOMER_SPECIFIC)}
            >
              {customerSpecificReport.friendlyName ?? companyName}
            </Button>
          )}
        </div>
      )}
    </>
  )
}
